import { Dispatch, SetStateAction } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { IncomeVerificationType } from '@brighte/validation';
import { AssistantFormFields } from '@components/Assistant';
import { OccupationField } from '@components/OccupationField';
import { Occupation } from '@graphql/index';
import { LABELS, ManualOccupationJobTitleMatchingType } from '../../utils';

type EmployedProps = Pick<
  UseFormReturn<IncomeVerificationType>,
  'control' | 'setValue' | 'register' | 'clearErrors'
> & {
  fields: string[];
  setOccupationIncomeThreshold: Dispatch<SetStateAction<number>>;
  occupations: Occupation[];
  isOccupationLoading: boolean;
  isOccupationFieldDisplayed: boolean;
  usingOpenBanking?: boolean;
  manualOccupationJobTitle: ManualOccupationJobTitleMatchingType;
  setManualOccupationJobTitle: Dispatch<SetStateAction<ManualOccupationJobTitleMatchingType>>;
};

type EmployedType = {
  fieldName: FieldPath<IncomeVerificationType>;
  label: string;
};

function Employed({
  fields,
  control,
  setValue,
  register,
  clearErrors,
  setOccupationIncomeThreshold,
  occupations,
  isOccupationLoading,
  isOccupationFieldDisplayed,
  usingOpenBanking,
  manualOccupationJobTitle,
  setManualOccupationJobTitle,
}: EmployedProps) {
  const { EMPLOYER_PHONE_NUMBER, JOB_TITLE, OCCUPATION_FIELD } = LABELS;
  const employedFields: EmployedType[] = [];

  if (usingOpenBanking === false) {
    employedFields.push({
      fieldName: 'applicant.employment.employed.employerPhoneNumber',
      label: EMPLOYER_PHONE_NUMBER,
    });
  }

  if (!isOccupationFieldDisplayed) {
    employedFields.push({
      fieldName: 'applicant.employment.employed.jobTitle',
      label: JOB_TITLE,
    });
  }

  return (
    <>
      <AssistantFormFields
        fields={[
          ...employedFields.filter(({ fieldName }) => fields.includes(fieldName)).map(({ fieldName }) => fieldName),
          ...(isOccupationFieldDisplayed && usingOpenBanking === false
            ? ['applicant.employment.employed.jobTitle']
            : []),
        ]}
      />
      {employedFields.map(({ fieldName, label }) =>
        fields.includes(fieldName) ? (
          <TextInputField key={fieldName} name={fieldName} control={control} label={label} />
        ) : null,
      )}
      {isOccupationFieldDisplayed && usingOpenBanking === false && (
        <OccupationField<IncomeVerificationType>
          setValue={setValue}
          clearErrors={clearErrors}
          register={register}
          control={control}
          name={`applicant.employment.employed` as FieldPath<IncomeVerificationType>}
          label={OCCUPATION_FIELD}
          setOccupationIncomeThreshold={setOccupationIncomeThreshold}
          occupations={occupations}
          isOccupationLoading={isOccupationLoading}
          manualOccupationJobTitle={manualOccupationJobTitle}
          setManualOccupationJobTitle={setManualOccupationJobTitle}
        />
      )}
    </>
  );
}

export default Employed;
